"use client";

import React, { useRef, useState, useCallback, useEffect, ReactNode } from "react";
import styles from "./styles.module.scss";
import Indicator from "./Indicator";

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  threshold?: number;
  resistance?: number;
  enabled?: boolean;
  pullText?: string;
  releaseText?: string;
  refreshingText?: string;
}

type PullState = "idle" | "pulling" | "ready" | "refreshing" | "returning";

export default function PullToRefresh({
  children,
  onRefresh,
  threshold = 80,
  resistance = 2.5,
  enabled = true,
  pullText = "Kéo xuống để làm mới",
  releaseText = "Thả ra để làm mới",
  refreshingText = "Đang làm mới...",
}: PullToRefreshProps) {
  const [pullState, setPullState] = useState<PullState>("idle");
  const [pullDistance, setPullDistance] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const scrollableElement = useRef<HTMLElement | null>(null);
  const touchStarted = useRef(false);

  // Check if element or its parents are scrollable
  const findScrollableParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
    if (!element || element === document.body) return null;

    const style = window.getComputedStyle(element);
    const hasOverflowStyle = style.overflowY === "auto" || style.overflowY === "scroll";
    const hasScrollableContent = element.scrollHeight > element.clientHeight;

    // Only consider it truly scrollable if it has both overflow style AND actual content to scroll
    // This prevents false positives from layout containers with scroll classes but no actual overflow
    if (hasOverflowStyle && hasScrollableContent) {
      return element;
    }

    return findScrollableParent(element.parentElement);
  }, []);

  // Check if we can start pull to refresh
  const canPullToRefresh = useCallback((): boolean => {
    if (!enabled) return false;

    // Check if page is at top
    if (window.scrollY > 0 || document.documentElement.scrollTop > 0 || document.body.scrollTop > 0) {
      return false;
    }

    // Check if scrollable child is at top
    if (scrollableElement.current && scrollableElement.current.scrollTop > 0) {
      return false;
    }

    return true;
  }, [enabled]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (pullState !== "idle" || !enabled) return;

    const touch = e.touches[0];
    startY.current = touch.clientY;
    currentY.current = touch.clientY;

    // Find scrollable parent
    const target = e.target as HTMLElement;
    scrollableElement.current = findScrollableParent(target);

    // Mark that touch has started, but don't determine if it's a pull gesture yet
    touchStarted.current = true;
    isDragging.current = false;
  }, [pullState, enabled, findScrollableParent]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!touchStarted.current) return;

    const touch = e.touches[0];
    currentY.current = touch.clientY;
    const deltaY = currentY.current - startY.current;

    // If we haven't determined if this is a pull gesture yet
    if (!isDragging.current) {
      // Only start pull-to-refresh if:
      // 1. User is pulling down (deltaY > 0)
      // 2. We're at the top of the page
      // 3. Either there's no scrollable element, or it's at the top
      if (deltaY > 5 && canPullToRefresh()) {
        // Check if there's a scrollable element that can be scrolled down
        if (scrollableElement.current && scrollableElement.current.scrollHeight > scrollableElement.current.clientHeight) {
          // There's scrollable content, don't start pull-to-refresh
          touchStarted.current = false;
          return;
        }
        // Start pull-to-refresh gesture
        isDragging.current = true;
      } else if (Math.abs(deltaY) > 5) {
        // User is scrolling up or there's scrollable content, not a pull gesture
        touchStarted.current = false;
        return;
      } else {
        // Movement too small to determine, wait for more movement
        return;
      }
    }

    // We're in a pull-to-refresh gesture
    if (isDragging.current) {
      // Check if scrollable child reached top during move
      if (scrollableElement.current && scrollableElement.current.scrollTop > 0) {
        isDragging.current = false;
        touchStarted.current = false;
        setPullDistance(0);
        setPullState("idle");
        return;
      }

      if (deltaY > 0 && canPullToRefresh()) {
        // Apply resistance
        const adjustedDistance = Math.min(deltaY / resistance, threshold * 2);

        setPullDistance(adjustedDistance);

        if (adjustedDistance >= threshold) {
          setPullState("ready");
        } else if (adjustedDistance > 10) {
          setPullState("pulling");
        }

        // Prevent default to avoid page bounce on iOS
        if (e.cancelable) {
          e.preventDefault();
        }
      } else if (deltaY <= 0) {
        // User is scrolling up
        isDragging.current = false;
        touchStarted.current = false;
        setPullDistance(0);
        setPullState("idle");
      }
    }
  }, [canPullToRefresh, resistance, threshold]);

  const handleTouchEnd = useCallback(async () => {
    touchStarted.current = false;

    if (!isDragging.current) return;

    isDragging.current = false;

    if (pullState === "ready") {
      setPullState("refreshing");
      setPullDistance(60); // Keep indicator visible during refresh

      try {
        await onRefresh();
      } catch (error) {
        console.error("Pull to refresh error:", error);
      } finally {
        setPullState("returning");
        setPullDistance(0);

        setTimeout(() => {
          setPullState("idle");
        }, 300); // Match transition duration
      }
    } else {
      setPullState("returning");
      setPullDistance(0);

      setTimeout(() => {
        setPullState("idle");
      }, 300);
    }
  }, [pullState, onRefresh]);

  // Add touch event listeners
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const options = { passive: false };

    container.addEventListener("touchstart", handleTouchStart, options);
    container.addEventListener("touchmove", handleTouchMove, options);
    container.addEventListener("touchend", handleTouchEnd, options);
    container.addEventListener("touchcancel", handleTouchEnd, options);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Determine the message to show
  const getMessage = () => {
    switch (pullState) {
      case "pulling":
        return pullText;
      case "ready":
        return releaseText;
      case "refreshing":
        return refreshingText;
      default:
        return pullText;
    }
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <Indicator
        pullDistance={pullDistance}
        pullState={pullState}
        threshold={threshold}
        message={getMessage()}
      />
      <div
        ref={contentRef}
        className={styles.content}
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: pullState === "returning" ? "transform 0.3s ease" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}